/**
 * 俯瞰編輯控制器
 *
 * - OrbitControls（右鍵旋轉、滾輪縮放、中鍵平移）
 * - 左鍵放置物件、Shift+左鍵刪除最近物件
 * - 光線投射到地面平面，查詢高度圖取得 Y 座標
 * - 預覽指示圈跟隨游標
 */

import { useRef, useEffect, useCallback, useMemo } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import * as THREE from 'three/webgpu'
import { useGameStore } from '../core/store/gameStore'
import {
  TERRAIN_WORLD_SIZE,
  TERRAIN_HEIGHT_SCALE,
  WATER_LEVEL,
} from '../core/terrainConstants'
import { terrainBuffer } from '../core/terrainBuffer'

export default function EditController() {
  const { camera, gl } = useThree()
  const controlsRef = useRef<any>(null)
  const previewRef = useRef<THREE.Group>(null)

  const addObject = useGameStore(s => s.addObject)
  const removeObject = useGameStore(s => s.removeObject)
  const placedObjects = useGameStore(s => s.placedObjects)
  const selectedType = useGameStore(s => s.selectedObjectType)
  const toolGroup = useGameStore(s => s.toolGroup)
  const addLabel = useGameStore(s => s.addLabel)
  const removeLabel = useGameStore(s => s.removeLabel)
  const mapLabels = useGameStore(s => s.mapLabels)

  // 地面平面用於光線投射（取 XZ 位置）
  const groundPlane = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), -WATER_LEVEL), [])
  const pointer = useRef(new THREE.Vector2())
  const hitPoint = useRef(new THREE.Vector3())
  const hasHit = useRef(false)

  // 判斷是點擊還是拖曳
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null)

  // 初始化俯瞰相機位置
  useEffect(() => {
    camera.position.set(0, 250, 120)
    camera.lookAt(0, 0, 0)
  }, [camera])

  // 追蹤游標螢幕座標
  const onPointerMove = useCallback((e: PointerEvent) => {
    const rect = gl.domElement.getBoundingClientRect()
    pointer.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    pointer.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
  }, [gl.domElement])

  // 記錄 pointerdown 位置
  const onPointerDown = useCallback((e: PointerEvent) => {
    if (e.button === 0) {
      pointerDownPos.current = { x: e.clientX, y: e.clientY }
    }
  }, [])

  // 點擊放置或刪除
  const onPointerUp = useCallback((e: PointerEvent) => {
    if (e.button !== 0 || !pointerDownPos.current) return
    const dx = e.clientX - pointerDownPos.current.x
    const dy = e.clientY - pointerDownPos.current.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    pointerDownPos.current = null

    // 拖曳超過 5px 不算點擊
    if (dist > 5) return
    if (!hasHit.current) return

    const pt = hitPoint.current

    if (e.shiftKey) {
      if (toolGroup === 'label') {
        // Shift+左鍵：刪除最近的標籤（20 單位內）
        let nearest: string | null = null
        let nearestDist = 20
        for (const l of mapLabels) {
          const d = Math.sqrt(
            (l.position[0] - pt.x) ** 2 + (l.position[2] - pt.z) ** 2,
          )
          if (d < nearestDist) {
            nearestDist = d
            nearest = l.id
          }
        }
        if (nearest) removeLabel(nearest)
      } else {
        // Shift+左鍵：刪除最近的物件（15 單位內）
        let nearest: string | null = null
        let nearestDist = 15
        for (const obj of placedObjects) {
          const d = Math.sqrt(
            (obj.position[0] - pt.x) ** 2 + (obj.position[2] - pt.z) ** 2,
          )
          if (d < nearestDist) {
            nearestDist = d
            nearest = obj.id
          }
        }
        if (nearest) removeObject(nearest)
      }
    } else if (toolGroup === 'label') {
      // 左鍵：放置新標籤
      const id = `label_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      addLabel({
        id,
        text: '未命名',
        position: [pt.x, pt.y, pt.z],
      })
    } else {
      // 左鍵：放置物件
      const id = `obj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      addObject({
        id,
        type: selectedType,
        position: [pt.x, pt.y, pt.z],
        rotation: Math.random() * Math.PI * 2,
        scale: 0.8 + Math.random() * 0.4,
      })
    }
  }, [selectedType, toolGroup, addObject, removeObject, placedObjects, addLabel, removeLabel, mapLabels])

  useEffect(() => {
    const el = gl.domElement
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('pointerup', onPointerUp)
    return () => {
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointerup', onPointerUp)
    }
  }, [gl.domElement, onPointerMove, onPointerDown, onPointerUp])

  // 每幀射線檢測地面位置
  const raycaster = useMemo(() => new THREE.Raycaster(), [])

  useFrame(() => {
    raycaster.setFromCamera(pointer.current, camera)
    const hit = new THREE.Vector3()
    if (raycaster.ray.intersectPlane(groundPlane, hit)) {
      // 查詢高度圖取得實際地形高度
      if (terrainBuffer.ready) {
        const terrainY = terrainBuffer.getHeight(hit.x, hit.z)
        hit.y = Math.max(terrainY, WATER_LEVEL)
      }
      hitPoint.current.copy(hit)
      hasHit.current = true
      if (previewRef.current) {
        previewRef.current.position.copy(hit)
        previewRef.current.visible = true
      }
    } else {
      hasHit.current = false
      if (previewRef.current) {
        previewRef.current.visible = false
      }
    }
  })

  const half = TERRAIN_WORLD_SIZE / 2

  return (
    <>
      <OrbitControls
        ref={controlsRef}
        makeDefault
        mouseButtons={{ LEFT: -1 as any, MIDDLE: 2, RIGHT: 0 }}
        enableDamping
        dampingFactor={0.12}
        maxPolarAngle={Math.PI / 2.8}
        minPolarAngle={0.2}
        minDistance={20}
        maxDistance={700}
        zoomSpeed={0.25}
        rotateSpeed={0.6}
        panSpeed={0.8}
        enablePan
        screenSpacePanning
        target={[0, 0, 0]}
      />
      {/* 放置預覽指示點 — 懸浮球體，避免與地形嵌合 */}
      <group ref={previewRef} visible={false}>
        <mesh position={[0, 2.5, 0]}>
          <sphereGeometry args={[0.8, 16, 16]} />
          <meshBasicMaterial color="#d0c8a0" transparent opacity={0.85} depthWrite={false} />
        </mesh>
        <mesh position={[0, 2.5, 0]} scale={[3, 3, 3]}>
          <sphereGeometry args={[0.8, 16, 16]} />
          <meshBasicMaterial color="#c8c090" transparent opacity={0.15} depthWrite={false} />
        </mesh>
      </group>
    </>
  )
}
